import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/constants';
import { ConfirmationModalService } from 'src/app/providers/services/confirmation-modal.service';
import { LoaderService } from 'src/app/providers/services/loader.service';
import { MissionsService } from 'src/app/providers/services/missions.service';
import { NavigationService } from 'src/app/providers/services/navigation.service';
import { TeamsService } from 'src/app/providers/services/teams.service';
import { Mission } from 'src/app/storage/models/mission.model';
import { TeamMission } from 'src/app/storage/models/team-mission.model';
import { Team } from 'src/app/storage/models/team.model';

@Component({
    selector: 'app-support-config',
    templateUrl: './support-config.component.html',
    styleUrls: ['./support-config.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SupportConfigComponent implements OnInit, OnDestroy {
  mission: Mission;
  missionSupportTeams: TeamMission[];

  runIteamTeams: Team[];

  addingTeam = false;

  rolename = CONSTANTS.TEAM.ROLES.REMOTE_SUPERVISION;

  private missionSubscription: Subscription;
  private teamsSubscription: Subscription;

  constructor(
    private navigationService: NavigationService,
    private missionsService: MissionsService,
    private teamsService: TeamsService,
    private translateService: TranslateService,
    private confirmationModalService: ConfirmationModalService,
    private loader: LoaderService
  ) { }

  ngOnInit(): void {
    this.missionSubscription = this.missionsService.selectedMission$.subscribe(async (mission: Mission) => {
      this.mission = mission;
      await this.getMissionSupportTeams();
    });
  }

  ngOnDestroy(): void {
    if (this.missionSubscription) { this.missionSubscription.unsubscribe(); }
    if (this.teamsSubscription) { this.teamsSubscription.unsubscribe(); }
  }

  async getMissionSupportTeams(): Promise<void> {
    if (this.teamsSubscription) { this.teamsSubscription.unsubscribe(); }
    
    this.loader.show();

    const runIteamTeams = await this.teamsService.getMissionTeams({
      missionId: this.mission.missionId,
      rolename: CONSTANTS.TEAM.ROLES.RUN_ITEAM
    });

    const missionSupportTeams = await this.teamsService.getMissionTeams({
      missionId: this.mission.missionId,
      rolename: CONSTANTS.TEAM.ROLES.REMOTE_SUPERVISION
    });

    this.loader.hide();

    if (missionSupportTeams == null || runIteamTeams == null) {
      await this.backToMissionConfig();
    } else {
      this.runIteamTeams = runIteamTeams;
      this.teamsService.missionSupportTeams$.next(missionSupportTeams);
    }

    this.teamsSubscription = this.teamsService.missionSupportTeams$.subscribe(async (missionTeams: TeamMission[]) => {
      this.missionSupportTeams = missionTeams;
    });
  }

  async backToMissionConfig() {
    await this.navigationService.navigateFromHome(CONSTANTS.ROUTES.MISSION_CONFIG);
  }

  openTeamModal(): void {
    this.addingTeam = true;
  }

  closeTeamModal(): void {
    this.addingTeam = false;
  }

  async addTeam(teamToAdd: Team): Promise<void> {
    const missionTeam = new TeamMission({
      ...teamToAdd,
      teammissionleader: 0,
      createdOn: Math.floor(Date.now() / 1000).toString(),
      createdBy: null,
      changedOn: null,
      changedBy: null
    });

    const isTeamAdded = await this.teamsService.addUpdateMissionTeam({
      missionId: this.mission.missionId,
      rolename: CONSTANTS.TEAM.ROLES.REMOTE_SUPERVISION,
      missionTeam: missionTeam
    });

    if (isTeamAdded) {
      // Add team to behavior subject
      this.teamsService.missionSupportTeams$.next(
        [...this.missionSupportTeams, missionTeam]
      );
    }
  }

  async removeTeam(team: TeamMission): Promise<void> {
    const isTeamRemoved = await this.teamsService.removeMissionTeam({
      missionId: this.mission.missionId,
      rolename: CONSTANTS.TEAM.ROLES.REMOTE_SUPERVISION,
      missionTeam: team
    });

    if (isTeamRemoved) {
      // Remove team from behavior subject
      this.teamsService.missionSupportTeams$.next(
        this.missionSupportTeams.filter(missionTeam => missionTeam.teamMemberId !== team.teamMemberId)
      );
    }
  }

  async toggleSupportStatus(): Promise<void> {
    const status = (this.mission.supportstatus >= CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED) ?
                    CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS :
                    CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED;

    // Confirm
    if (status === CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED ) {
      const translations = await this.translateService.get('SUPPORT').toPromise();
      let title = `<strong>${translations['STATUS_COMPLETE']}</strong>`;

      // Open confirmation modal
      this.confirmationModalService.openModal({
        title: title,
        buttons: [
          {
            text: await this.translateService.get('CONFIRM').toPromise(),
            method: async () => {
              this.confirmationModalService.closeModal();
              await this.setSupportStatus(status);
            }
          },
          {
            text: await this.translateService.get('CANCEL').toPromise(),
            method: async () => { this.confirmationModalService.closeModal(); }
          }
        ]
      });
    } else {
      await this.setSupportStatus(status);
    }
  }

  async setSupportStatus(status: number): Promise<void> {
    // Update support status
    const isStatusUpdated = await this.missionsService.updateMissionSupportStatus(this.mission.missionId, status);

    // If update succeeded, set mission support status
    if (isStatusUpdated) {
      this.mission.supportstatus = status;
    }
  }

  isSupportComplete(): boolean {
    return this.mission.supportstatus >= CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED;
  }


}
